import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "fixed inset-0 overflow-y-auto" }
const _hoisted_2 = { class: "flex min-h-full items-center justify-center p-4 text-center" }
const _hoisted_3 = { class: "flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700" }
const _hoisted_4 = { class: "p-6 space-y-6" }
const _hoisted_5 = {
  key: 0,
  class: "rounded-xl bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800"
}
const _hoisted_6 = { class: "flex" }
const _hoisted_7 = { class: "ml-3" }
const _hoisted_8 = { class: "text-sm text-red-700 dark:text-red-400" }
const _hoisted_9 = {
  key: 1,
  class: "rounded-xl bg-green-50 dark:bg-green-900/20 p-4 border border-green-200 dark:border-green-800"
}
const _hoisted_10 = { class: "flex" }
const _hoisted_11 = { class: "ml-3" }
const _hoisted_12 = { class: "text-sm text-green-700 dark:text-green-400" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = {
  key: 0,
  class: "space-y-2"
}
const _hoisted_15 = { class: "relative" }
const _hoisted_16 = { class: "space-y-2" }
const _hoisted_17 = { class: "relative" }
const _hoisted_18 = { class: "space-y-2" }
const _hoisted_19 = { class: "relative" }
const _hoisted_20 = ["minlength"]
const _hoisted_21 = ["disabled"]
const _hoisted_22 = {
  key: 0,
  class: "flex items-center"
}
const _hoisted_23 = { key: 1 }
const _hoisted_24 = { class: "text-center" }
const _hoisted_25 = {
  key: 2,
  class: "text-center"
}
const _hoisted_26 = {
  key: 3,
  class: "p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600"
}
const _hoisted_27 = { class: "relative" }
const _hoisted_28 = { class: "flex gap-2" }
const _hoisted_29 = ["disabled"]
const _hoisted_30 = { class: "pt-4 border-t border-gray-200 dark:border-gray-600" }
const _hoisted_31 = ["disabled"]

import { ref, reactive } from 'vue';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/vue/24/outline'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import { authService, type AuthUser } from '../firebase/services/authService';

interface Props {
  isOpen: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success', user: AuthUser): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AuthModal',
  props: {
    isOpen: { type: Boolean }
  },
  emits: ["close", "success"],
  setup(__props: any, { emit: __emit }) {


const emit = __emit;

// Component state
const mode = ref<'signin' | 'signup'>('signin');
const loading = ref(false);
const error = ref('');
const success = ref('');
const showForgotPasswordForm = ref(false);
const forgotPasswordEmail = ref('');

// Form data
const form = reactive({
  email: '',
  password: '',
  displayName: ''
});

// Methods
const closeModal = () => {
  resetForm();
  emit('close');
};

const resetForm = () => {
  form.email = '';
  form.password = '';
  form.displayName = '';
  error.value = '';
  success.value = '';
  showForgotPasswordForm.value = false;
  forgotPasswordEmail.value = '';
  mode.value = 'signin';
};

const toggleMode = () => {
  mode.value = mode.value === 'signin' ? 'signup' : 'signin';
  error.value = '';
  success.value = '';
};

const showForgotPassword = () => {
  showForgotPasswordForm.value = true;
  forgotPasswordEmail.value = form.email;
};

const handleEmailAuth = async () => {
  if (loading.value) return;
  
  loading.value = true;
  error.value = '';
  success.value = '';

  try {
    let user;
    if (mode.value === 'signin') {
      user = await authService.signInWithEmail(form.email, form.password);
    } else {
      user = await authService.createAccountWithEmail(
        form.email, 
        form.password, 
        form.displayName
      );
    }
    
    success.value = mode.value === 'signin' ? 'Signed in successfully!' : 'Account created successfully!';
    emit('success', user);
    
    // Close modal after a short delay
    setTimeout(() => {
      closeModal();
    }, 1000);
    
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : 'An error occurred';
  } finally {
    loading.value = false;
  }
};

const signInWithGoogle = async () => {
  if (loading.value) return;
  
  loading.value = true;
  error.value = '';
  success.value = '';

  try {
    const user = await authService.signInWithGoogle();
    success.value = 'Signed in with Google successfully!';
    emit('success', user);
    
    // Close modal after a short delay
    setTimeout(() => {
      closeModal();
    }, 1000);
    
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : 'An error occurred';
  } finally {
    loading.value = false;
  }
};

const browseAsGuest = () => {
  if (loading.value) return;

  error.value = '';
  success.value = 'Continuing without an account';

  // Close modal after a short delay so the user can keep browsing without Firebase Auth.
  setTimeout(() => {
    closeModal();
  }, 600);
};

const handleForgotPassword = async () => {
  if (loading.value || !forgotPasswordEmail.value) return;
  
  loading.value = true;
  error.value = '';
  success.value = '';

  try {
    await authService.sendPasswordResetEmail(forgotPasswordEmail.value);
    success.value = 'Password reset email sent! Check your inbox.';
    showForgotPasswordForm.value = false;
  } catch (err: unknown) {
    error.value = err instanceof Error ? err.message : 'An error occurred';
  } finally {
    loading.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(TransitionRoot), {
    appear: "",
    show: _ctx.isOpen,
    as: "template"
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(Dialog), {
        as: "div",
        onClose: closeModal,
        class: "relative z-50"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(TransitionChild), {
            as: "template",
            enter: "duration-300 ease-out",
            "enter-from": "opacity-0",
            "enter-to": "opacity-100",
            leave: "duration-200 ease-in",
            "leave-from": "opacity-100",
            "leave-to": "opacity-0"
          }, {
            default: _withCtx(() => _cache[5] || (_cache[5] = [
              _createElementVNode("div", { class: "fixed inset-0 bg-black/25 backdrop-blur-sm" }, null, -1)
            ])),
            _: 1,
            __: [5]
          }),
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(TransitionChild), {
                as: "template",
                enter: "duration-300 ease-out",
                "enter-from": "opacity-0 scale-95",
                "enter-to": "opacity-100 scale-100",
                leave: "duration-200 ease-in",
                "leave-from": "opacity-100 scale-100",
                "leave-to": "opacity-0 scale-95"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DialogPanel), { class: "w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 text-left align-middle shadow-xl transition-all border border-gray-200 dark:border-gray-700" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_3, [
                        _createVNode(_unref(DialogTitle), {
                          as: "h3",
                          class: "text-xl font-semibold text-gray-900 dark:text-white"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(mode.value === 'signin' ? 'Sign In' : 'Create Account'), 1)
                          ]),
                          _: 1
                        }),
                        _createElementVNode("button", {
                          type: "button",
                          class: "rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-colors",
                          onClick: closeModal
                        }, [
                          _createVNode(_unref(XMarkIcon), { class: "h-5 w-5" })
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_4, [
                        (error.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                              _createElementVNode("div", _hoisted_6, [
                                _createVNode(_unref(ExclamationTriangleIcon), { class: "h-5 w-5 text-red-400 flex-shrink-0" }),
                                _createElementVNode("div", _hoisted_7, [
                                  _createElementVNode("p", _hoisted_8, _toDisplayString(error.value), 1)
                                ])
                              ])
                            ]))
                          : _createCommentVNode("", true),
                        (success.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                              _createElementVNode("div", _hoisted_10, [
                                _createVNode(_unref(CheckCircleIcon), { class: "h-5 w-5 text-green-400 flex-shrink-0" }),
                                _createElementVNode("div", _hoisted_11, [
                                  _createElementVNode("p", _hoisted_12, _toDisplayString(success.value), 1)
                                ])
                              ])
                            ]))
                          : _createCommentVNode("", true),
                        _createElementVNode("button", {
                          type: "button",
                          class: "w-full flex justify-center items-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200",
                          onClick: signInWithGoogle,
                          disabled: loading.value
                        }, [
                          _cache[6] || (_cache[6] = _createElementVNode("svg", {
                            class: "w-5 h-5 mr-3",
                            viewBox: "0 0 24 24"
                          }, [
                            _createElementVNode("path", {
                              fill: "#4285F4",
                              d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            }),
                            _createElementVNode("path", {
                              fill: "#34A853",
                              d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            }),
                            _createElementVNode("path", {
                              fill: "#FBBC05",
                              d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            }),
                            _createElementVNode("path", {
                              fill: "#EA4335",
                              d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            })
                          ], -1)),
                          _createTextVNode(" " + _toDisplayString(loading.value ? 'Signing in...' : 'Continue with Google'), 1)
                        ], 8, _hoisted_13),
                        _cache[13] || (_cache[13] = _createElementVNode("div", { class: "relative" }, [
                          _createElementVNode("div", { class: "absolute inset-0 flex items-center" }, [
                            _createElementVNode("div", { class: "w-full border-t border-gray-300 dark:border-gray-600" })
                          ]),
                          _createElementVNode("div", { class: "relative flex justify-center text-sm" }, [
                            _createElementVNode("span", { class: "px-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400" }, "Or continue with email")
                          ])
                        ], -1)),
                        _createElementVNode("form", {
                          onSubmit: _withModifiers(handleEmailAuth, ["prevent"]),
                          class: "space-y-4"
                        }, [
                          (mode.value === 'signup')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                _cache[7] || (_cache[7] = _createElementVNode("label", {
                                  for: "displayName",
                                  class: "block text-sm font-semibold text-gray-900 dark:text-white"
                                }, " Display Name ", -1)),
                                _createElementVNode("div", _hoisted_15, [
                                  _withDirectives(_createElementVNode("input", {
                                    id: "displayName",
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.displayName) = $event)),
                                    type: "text",
                                    class: "block w-full rounded-xl border-0 py-3 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 dark:focus:ring-purple-500 transition-all duration-200 text-sm",
                                    placeholder: "Your display name"
                                  }, null, 512), [
                                    [_vModelText, form.displayName]
                                  ])
                                ])
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_16, [
                            _cache[8] || (_cache[8] = _createElementVNode("label", {
                              for: "email",
                              class: "block text-sm font-semibold text-gray-900 dark:text-white"
                            }, " Email Address ", -1)),
                            _createElementVNode("div", _hoisted_17, [
                              _withDirectives(_createElementVNode("input", {
                                id: "email",
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.email) = $event)),
                                type: "email",
                                required: "",
                                class: "block w-full rounded-xl border-0 py-3 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 dark:focus:ring-purple-500 transition-all duration-200 text-sm",
                                placeholder: "your@email.com"
                              }, null, 512), [
                                [_vModelText, form.email]
                              ])
                            ])
                          ]),
                          _createElementVNode("div", _hoisted_18, [
                            _cache[9] || (_cache[9] = _createElementVNode("label", {
                              for: "password",
                              class: "block text-sm font-semibold text-gray-900 dark:text-white"
                            }, " Password ", -1)),
                            _createElementVNode("div", _hoisted_19, [
                              _withDirectives(_createElementVNode("input", {
                                id: "password",
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.password) = $event)),
                                type: "password",
                                required: "",
                                class: "block w-full rounded-xl border-0 py-3 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 dark:focus:ring-purple-500 transition-all duration-200 text-sm",
                                placeholder: "••••••••",
                                minlength: mode.value === 'signup' ? 6 : undefined
                              }, null, 8, _hoisted_20), [
                                [_vModelText, form.password]
                              ])
                            ])
                          ]),
                          _createElementVNode("button", {
                            type: "submit",
                            class: "w-full inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200",
                            disabled: loading.value
                          }, [
                            (loading.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_22, _cache[10] || (_cache[10] = [
                                  _createElementVNode("div", { class: "animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" }, null, -1),
                                  _createTextVNode(" Please wait... ", -1)
                                ])))
                              : (_openBlock(), _createElementBlock("span", _hoisted_23, _toDisplayString(mode.value === 'signin' ? 'Sign In' : 'Create Account'), 1))
                          ], 8, _hoisted_21)
                        ], 32),
                        _createElementVNode("div", _hoisted_24, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "text-sm text-purple-600 dark:text-purple-400 hover:text-purple-500 dark:hover:text-purple-300 font-medium transition-colors",
                            onClick: toggleMode
                          }, _toDisplayString(mode.value === 'signin' ? "Don't have an account? Sign up" : "Already have an account? Sign in"), 1)
                        ]),
                        (mode.value === 'signin')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                              _createElementVNode("button", {
                                type: "button",
                                class: "text-sm text-gray-600 dark:text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors",
                                onClick: showForgotPassword
                              }, " Forgot your password? ")
                            ]))
                          : _createCommentVNode("", true),
                        (showForgotPasswordForm.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_26, [
                              _cache[11] || (_cache[11] = _createElementVNode("h4", { class: "text-sm font-semibold text-gray-900 dark:text-white mb-3" }, "Reset Password", -1)),
                              _createElementVNode("form", {
                                onSubmit: _withModifiers(handleForgotPassword, ["prevent"]),
                                class: "space-y-3"
                              }, [
                                _createElementVNode("div", _hoisted_27, [
                                  _withDirectives(_createElementVNode("input", {
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((forgotPasswordEmail).value = $event)),
                                    type: "email",
                                    required: "",
                                    class: "block w-full rounded-xl border-0 py-3 px-4 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 dark:focus:ring-purple-500 transition-all duration-200 text-sm",
                                    placeholder: "Enter your email address"
                                  }, null, 512), [
                                    [_vModelText, forgotPasswordEmail.value]
                                  ])
                                ]),
                                _createElementVNode("div", _hoisted_28, [
                                  _createElementVNode("button", {
                                    type: "submit",
                                    class: "flex-1 inline-flex justify-center items-center px-4 py-2.5 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 transition-all duration-200",
                                    disabled: loading.value
                                  }, " Send Reset Email ", 8, _hoisted_29),
                                  _createElementVNode("button", {
                                    type: "button",
                                    class: "px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200",
                                    onClick: _cache[4] || (_cache[4] = ($event: any) => (showForgotPasswordForm.value = false))
                                  }, " Cancel ")
                                ])
                              ], 32)
                            ]))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_30, [
                          _createElementVNode("button", {
                            type: "button",
                            class: "w-full flex justify-center items-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200",
                            onClick: browseAsGuest,
                            disabled: loading.value
                          }, " Continue without an account ", 8, _hoisted_31),
                          _cache[12] || (_cache[12] = _createElementVNode("p", { class: "mt-2 text-xs text-gray-500 dark:text-gray-400 text-center" }, " Browse wallpapers without creating a Firebase Auth account ", -1))
                        ])
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["show"]))
}
}

})